import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { ventures } from "@/data/portfolio";

/**
 * Footer.
 *
 * Three things were wrong with the previous version and all three are worth
 * recording, because they are the reasons it read as cheap.
 *
 *  1. It was cream on cream with 14px ink2 text, so it simply disappeared. A
 *     footer is the terminal weight of a page. It is now a dark slab, which
 *     also closes the document instead of letting it fade out.
 *
 *  2. It carried a SECOND wordmark: her name at 60px in running display serif,
 *     a completely different treatment from the small caps lockup in the nav.
 *     One person, two marks. The name is now set in the same lockup language.
 *
 *  3. Every link was written `block` and rendered inline-block, because
 *     .editorial-link was declared after @tailwind utilities and overrode it.
 *     So "Mae Terra" and "Earth Alive" sat on the same line with no separator
 *     and read as "Mae TerraEarth Alive". Fixed at the source in index.css;
 *     these are explicit flex columns now so it cannot regress.
 *
 * "Crafted with care" is gone. It is the exact templated phrasing the whole
 * rebuild is trying to get away from.
 */

const COL = "flex flex-col items-start gap-2.5";
const LINK =
  "font-sans text-sm text-cream/70 hover:text-cream transition-colors duration-300 editorial-link";
const LABEL =
  "label text-cream/45 mb-1";

export const Footer = () => {
  /**
   * The closing ask only renders where there is not already one on the page.
   *
   * The enquiry now lives at /contact, so the ask renders everywhere except
   * there, where the page itself is the ask.
   */
  // Hide the closing ask only where the page itself is the ask.
  const onContact = useLocation().pathname === "/contact";

  return (
  <footer className="bg-ink text-cream" data-testid="site-footer">
    {!onContact && (
    <div className="border-b border-cream/15" data-testid="footer-closing-ask">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-16 md:py-20 grid grid-cols-12 gap-8 items-end">
        <div className="col-span-12 lg:col-span-8">
          <p className="label text-gold">
            Next step
          </p>
          <p className="mt-5 font-display text-display-l text-cream max-w-[26ch]">
            Tell me what you are building.
          </p>
        </div>
        <div className="col-span-12 lg:col-span-4 lg:justify-self-end flex flex-wrap gap-3">
          <Link
            to="/contact"
            data-testid="footer-cta-project"
            data-cursor="hover"
            className="group inline-flex items-center gap-3 px-6 py-4 bg-cream text-ink label hover:bg-gold transition-colors duration-500"
          >
            Start a project
            <ArrowUpRight
              size={14}
              className="transition-transform duration-300 group-hover:rotate-45"
            />
          </Link>
          <Link
            to="/stay"
            data-testid="footer-cta-stay"
            data-cursor="hover"
            className="inline-flex items-center gap-3 px-6 py-4 border border-cream/40 text-cream label hover:bg-cream hover:text-ink transition-colors duration-500"
          >
            Book a stay
          </Link>
        </div>
      </div>
    </div>
    )}

    <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-14 md:py-20 grid grid-cols-12 gap-y-12 gap-x-6 md:gap-x-10">
      {/* Identity, in the same language as the nav lockup */}
      <div className="col-span-12 md:col-span-4">
        <p
          className="font-display uppercase text-[17px] text-cream"
          style={{ letterSpacing: "0.19em", fontWeight: 500 }}
        >
          Shruthi Ravi
        </p>
        <p
          className="mt-2.5 font-mono uppercase text-[9px] text-cream/50"
          style={{ letterSpacing: "0.3em", fontWeight: 500 }}
        >
          Architecture · Interiors
        </p>
        <p className="mt-7 font-sans font-light text-sm text-cream/65 max-w-[38ch] leading-relaxed">
          Heritage restoration, private residences, and four hospitality
          businesses you can book directly.
        </p>
        <p className="mt-4 label text-cream/45">
          Bangalore · Goa · Austin
        </p>
      </div>

      <nav className="col-span-6 md:col-span-2" aria-label="Places">
        <p className={LABEL}>Places</p>
        <div className={COL}>
          {ventures.map((v) => (
            <Link
              key={v.slug}
              to={`/ventures/${v.slug}`}
              className={LINK}
              data-testid={`footer-${v.slug}`}
            >
              {v.title}
            </Link>
          ))}
        </div>
      </nav>

      <nav className="col-span-6 md:col-span-2" aria-label="Studio">
        <p className={LABEL}>Studio</p>
        <div className={COL}>
          <Link to="/work" className={LINK} data-testid="footer-work">
            Selected works
          </Link>
          <Link to="/services" className={LINK} data-testid="footer-services">
            Services
          </Link>
          <Link to="/about#talk" className={LINK} data-testid="footer-talk">
            The TEDx talk
          </Link>
          <Link to="/press" className={LINK} data-testid="footer-press-link">
            Press
          </Link>
        </div>
      </nav>

      <div className="col-span-12 md:col-span-4 md:justify-self-end md:text-right">
        <p className={`${LABEL} md:text-right`}>Direct</p>
        <div className={`${COL} md:items-end`}>
          <a
            href="mailto:shruthiravioli@gmail.com"
            className={LINK}
            data-testid="footer-email"
          >
            shruthiravioli@gmail.com
          </a>
          <a href="tel:+13128682030" className={LINK} data-testid="footer-phone">
            +1 312 868 2030
          </a>
          <a
            href="https://www.instagram.com/shruthi_shakirra/"
            target="_blank"
            rel="noopener"
            className={LINK}
            data-testid="footer-instagram"
          >
            Instagram
          </a>
        </div>
      </div>
    </div>

    <div className="border-t border-cream/15">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-6 flex flex-col md:flex-row md:items-center justify-between gap-2 label text-cream/45">
        <span>© {new Date().getFullYear()} Shruthi Ravi</span>
        <span>Bangalore · Goa · Austin</span>
      </div>
    </div>
  </footer>
  );
};
